/**
 * Queue Manager for App Integration Settings
 *
 * Handles background processing of table rows that require queue processing.
 * Works with the uap-app-integration-settings-form component for submissions.
 *
 * Supports integration ID overrides via QueueConfig.integrationId for Pro/third-party integrations.
 *
 * Example usage:
 * ```javascript
 * // For Pro integrations that need a different integration ID
 * const queueConfig = {
 *   postValues: { automator_action: 'notion_pro_webhook_url_regeneration' },
 *   integrationId: 'notion-pro' // Override form's default 'notion' ID
 * };
 * ```
 */
import { collectFormData } from '../../../utils/form-data-collector';
/**
 * Queue Manager
 *
 * Processes queued table rows sequentially with rate limiting and loading state management.
 */
export class QueueManager {
    constructor(onCompleteCallback, setRowLoading) {
        this.isProcessing = false;
        this.onComplete = onCompleteCallback;
        this.setRowLoading = setRowLoading;
    }
    /**
     * Process all queued rows in sequence
     *
     * @param form - The form element to submit to
     * @param rows - All table rows (queued rows will be filtered)
     * @param queueConfig - Queue configuration (rate limiting, etc)
     * @param onRowUpdate - Callback to update the table's data
     * @param component - The settings table component instance
     */
    async processQueue(form, rows, queueConfig, onRowUpdate, component) {
        if (this.isProcessing) {
            return;
        }
        this.isProcessing = true;
        try {
            // Get rows that need processing
            const rowsToProcess = rows.filter(row => row.queue);
            if (rowsToProcess.length === 0) {
                return;
            }
            // Set loading state for all queued rows
            rowsToProcess.forEach(row => {
                if (row.id) {
                    this.setRowLoading?.(row.id, true);
                }
            });
            // Process each row sequentially
            for (const row of rowsToProcess) {
                if (!row.id) {
                    console.warn('Skipping row without ID');
                    continue;
                }
                try {
                    // Look for the row element in the component's shadow DOM
                    const rowEl = component.renderRoot.querySelector(`[data-row-id="${row.id}"]`);
                    if (!rowEl) {
                        console.warn(`Row element not found for ID: ${row.id}`);
                        continue;
                    }
                    // Process the row using the form wrapper's submitToRest method
                    await this.processQueueItem(form, row, queueConfig, rowEl);
                }
                catch (error) {
                    // Parent component will handle this.
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    console.error(`Error processing row ${row.id}:`, errorMessage);
                }
                finally {
                    // Clear loading state for this row as soon as it's done
                    this.setRowLoading?.(row.id, false);
                }
                // Add rate limiting if configured
                if (queueConfig.rateLimit) {
                    await new Promise(resolve => setTimeout(resolve, queueConfig.rateLimit));
                }
            }
        }
        finally {
            this.isProcessing = false;
            this.onComplete?.();
        }
    }
    /**
     * Process a single row in the queue
     *
     * @param form - The form element to submit to
     * @param row - The row to process
     * @param queueConfig - Queue configuration (post values, etc)
     * @param rowEl - The row element
     */
    async processQueueItem(form, row, queueConfig, rowEl) {
        // Set RowSubmission for internal event handling
        const rowSubmission = {
            rowId: row.id,
            rowElement: rowEl
        };
        // Collect form data with row submission context
        const formData = collectFormData(form, {
            includeHidden: false,
            rowSubmission: rowSubmission
        });
        // Add row ID and queue config values
        formData.set('row_id', row.id);
        if (queueConfig.postValues) {
            Object.entries(queueConfig.postValues).forEach(([key, value]) => {
                formData.set(key, value);
            });
        }
        // Ensure we have an action - use the first post value action or default
        if (!formData.has('automator_action') && queueConfig.postValues?.automator_action) {
            formData.set('automator_action', queueConfig.postValues.automator_action);
        }
        // Submit via form component
        const formComponent = form.closest('uap-app-integration-settings-form');
        if (!formComponent) {
            throw new Error('No form component found');
        }
        // Form component handles response (alerts, notices, data updates)
        await formComponent.submitToRest(formData, queueConfig.integrationId);
    }
}
